import { User } from '@/utils/auth/auth0'
import { EMAIL_REGEX } from '@/utils/validation'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import GenerateProfileButton from '../../components/GenerateProfileButton'
import QuestionManager from '../../components/QuestionManager'
import QuestionDiffChecker from '../../components/QuestionDiffChecker'
import {
  useList,
  useGetIdentity,
  useCustomMutation,
  useNotification,
} from '@refinedev/core'
import { useForm } from '@refinedev/react-hook-form'
import { Controller } from 'react-hook-form'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import { Edit } from '@refinedev/mui'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { UUID } from 'crypto'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, isValid } from 'date-fns'
import { FLAG_OPTIONS } from '../../components/FlagOptions'
import { OpportunityTag } from '@/interfaces/categories'
import React from 'react'
import TestProfile from '../../components/TestProfile'
import {
  validatePositiveAmount,
  validateAmountRange,
  normalizeAmounts,
  normalizeFundName,
} from '@/utils/opportunityValidation'
import { IwiField } from '@/components/opportunities/fields/IwiField'
import { Funder } from '@/interfaces/opportunities'
import { useIwiSelection } from '@/hooks/useIwiSelection'
import { useReferenceOptions } from '@/hooks/useReferenceOptions'
import { normalizeUrl } from '@/utils/urlUtils'
import {
  calculateSuggestedCheckInDate,
  getEarliestUpcomingRoundWithOpenDate,
} from '@/utils/dataUtils'
import { StructuredQuestion } from '@/interfaces/questions'
import { findQuestionInRawText } from '@/utils/questionHighlight'
import HighlightedTextarea from '@/components/HighlightedTextarea'
import { filterTagOptions } from '@/utils/dataUtils'

interface FormValues {
  uuid: UUID | undefined
  revision_version: number | undefined
  funder_uuid: UUID | undefined
  fund_name: string
  always_open: boolean
  amount: string
  min_amount: string | null
  max_amount: string | null
  no_min_max_amount: boolean
  criteria: string
  description: string
  exclusions: string
  is_relational: boolean
  notes: string
  opportunity_uuid: UUID | undefined
  questions: string[]
  timing: string
  profile_text: string
  draft_profile_text: string
  application_info: string
  application_link: string
  opportunity_link: string
  references: string
  flag_for_updates: string
  next_check_in_date: Date | null
  questions_last_checked_at: Date | null
  gambling_proceeds: boolean
  is_international: boolean
  only_funds_registered_charities: boolean
  max_rank_is_4: boolean
  tags: string[]
  general_info: string
  iwi: number[]
  structured_questions: StructuredQuestion[]
  email_address: string | null
}

interface OpportunityTagResponse {
  tags: string[]
  reasoning: string
}

export default function OpportunityEdit() {
  const user = useGetIdentity().data as User

  const {
    saveButtonProps,
    register,
    control,
    formState: { errors, dirtyFields },
    refineCore: { query, onFinish },
    getValues,
    watch,
    setValue,
    reset,
  } = useForm({
    warnWhenUnsavedChanges: true,
    refineCoreProps: {
      redirect: 'show',
      resource: 'opportunities',
      meta: {
        updated_by:
          user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`,
      },
      errorNotification: (error) => {
        const rawMessage =
          (error as any)?.response?.data?.detail ||
          (error as any)?.response?.data?.message ||
          (error as any)?.message

        if (rawMessage?.toLowerCase().includes('already exists')) {
          return {
            message: 'Duplicate Opportunity',
            description: rawMessage,
            type: 'error' as const,
          }
        }

        return {
          message: 'Error saving opportunity',
          description:
            rawMessage || 'An unexpected error occurred. Please try again.',
          type: 'error' as const,
        }
      },
    },
  })

  const defaultValues = query?.data?.data

  // Use custom hooks
  const currentFunderUuid = watch('funder_uuid')
  const flagForUpdatesValue = watch('flag_for_updates')
  const nextCheckInDateValue = watch('next_check_in_date')
  const flagAllowsNullDate = flagForUpdatesValue === 'Inactive'
  const alwaysOpenValue = watch('always_open')

  const { data: funderListData, isLoading: funderListIsLoading } =
    useList<Funder>({
      resource: 'funder-options',
    })
  const funderOptions = funderListData?.data || []

  const currentFunder = useMemo(() => {
    if (!currentFunderUuid) return null
    return funderOptions.find((f) => f.uuid === currentFunderUuid) ?? null
  }, [currentFunderUuid, funderOptions])
  const { data: categories, isLoading: categoriesIsLoading } = useList({
    resource: 'opportunity_categories',
    config: {
      pagination: { current: 1, pageSize: 100 },
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting categories',
        description:
          error?.message || 'An error occurred while fetching categories',
        type: 'error',
      }
    },
  })

  // funding rounds to calculate suggested check-in date
  const { data: fundingRoundsData } = useList({
    resource: 'funding-rounds',
    meta: {
      opportunity_uuid: defaultValues?.uuid,
    },
    queryOptions: {
      enabled: !!defaultValues?.uuid,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting funding rounds',
        description:
          error?.message || 'An error occurred while fetching funding rounds',
        type: 'error',
      }
    },
  })

  // Use IWI selection hook
  const iwiValue = watch('iwi')
  const { iwiList, selectedIwi } = useIwiSelection(iwiValue)
  const [inheritIwi, setInheritIwi] = useState<boolean>(
    defaultValues?.inherit_iwi_from_funder ?? true,
  )

  const categoriesList = useMemo(
    () => (categories?.data as OpportunityTag[]) || [],
    [categories?.data],
  )

  const [values, setValues] = useState<FormValues>({
    uuid: undefined,
    revision_version: undefined,
    funder_uuid: undefined,
    fund_name: '',
    always_open: false,
    amount: '',
    min_amount: '',
    max_amount: '',
    no_min_max_amount: false,
    criteria: '',
    description: '',
    exclusions: '',
    is_relational: false,
    notes: '',
    opportunity_uuid: undefined,
    questions: [],
    timing: '',
    profile_text: '',
    draft_profile_text: '',
    application_info: '',
    references: '',
    opportunity_link: '',
    flag_for_updates: '',
    next_check_in_date: null,
    questions_last_checked_at: null,
    application_link: '',
    gambling_proceeds: false,
    is_international: false,
    only_funds_registered_charities: false,
    max_rank_is_4: false,
    tags: [],
    general_info: '',
    iwi: [],
    structured_questions: [],
    email_address: null,
  })

  const [missingFieldsDialogOpen, setMissingFieldsDialogOpen] = useState(false)

  const [showCustomFlag, setShowCustomFlag] = useState(false)
  const [customFlagValue, setCustomFlagValue] = useState('')
  const customFlagInputRef = useRef<HTMLInputElement>(null)
  const [dateDialogOpen, setDateDialogOpen] = useState(false)
  const [testProfileFormOpen, setTestProfileFormOpen] = useState(false)

  const [tagResults, setTagResults] = useState<OpportunityTagResponse | null>()
  const [generating, setGenerating] = useState(false)
  const [tagsAccepted, setTagsAccepted] = useState(false)
  const [tagGenerationError, setTagGenerationError] = useState<string | null>(
    null,
  )

  const [structuredQuestions, setStructuredQuestions] = useState<
    StructuredQuestion[]
  >(defaultValues?.structured_questions || [])
  const structuredQuestionsEditedRef = useRef(false)
  const [highlightRange, setHighlightRange] = useState<{
    start: number
    end: number
  } | null>(null)

  // Calculate highlight range when active question changes
  const handleActiveQuestionChange = useCallback(
    (question: StructuredQuestion | null) => {
      if (!question) {
        setHighlightRange(null)
        return
      }

      const rawText = (values.questions || []).join('\n\n')
      const range = findQuestionInRawText(question.question, rawText)
      setHighlightRange(range)
    },
    [values.questions],
  )

  //  message for default check-in date in missing fields dialog
  const defaultCheckInDateMessage = useMemo(() => {
    const rounds = (fundingRoundsData?.data || []) as Array<{
      application_open_date?: string | null
    }>
    const earliestRound = getEarliestUpcomingRoundWithOpenDate(rounds)

    if (earliestRound && !alwaysOpenValue) {
      const openDate = new Date(earliestRound.application_open_date)
      const suggestedDate = calculateSuggestedCheckInDate(
        rounds,
        alwaysOpenValue,
      )

      // check if suggested date is actually 14 days before (not fallback)
      const fourteenDaysBefore = new Date(openDate)
      fourteenDaysBefore.setDate(fourteenDaysBefore.getDate() - 14)

      if (suggestedDate.toDateString() === fourteenDaysBefore.toDateString()) {
        return (
          <>
            {format(suggestedDate, 'dd/MM/yyyy')}
            <br />
            (14 days before next funding round opens)
          </>
        )
      }
    }
    return '6 months from now'
  }, [fundingRoundsData?.data, alwaysOpenValue])

  const { open: openNotification } = useNotification()

  useEffect(() => {
    if (defaultValues) {
      // Create a clean copy with proper defaults for all fields
      const newValues = {
        uuid: defaultValues.uuid,
        revision_version: defaultValues.revision_version,
        funder_uuid: defaultValues.funder_uuid,
        fund_name: defaultValues.fund_name || '',
        always_open: defaultValues.always_open || false,
        amount: defaultValues.amount || '',
        min_amount: defaultValues.min_amount || '',
        max_amount: defaultValues.max_amount || '',
        no_min_max_amount: defaultValues.no_min_max_amount || false,
        criteria: defaultValues.criteria || '',
        description: defaultValues.description || '',
        exclusions: defaultValues.exclusions || '',
        is_relational: defaultValues.is_relational || false,
        notes: defaultValues.notes || '',
        opportunity_uuid: defaultValues.opportunity_uuid,
        questions: defaultValues.questions || [],
        timing: defaultValues.timing || '',
        profile_text: defaultValues.profile_text || '',
        draft_profile_text: defaultValues.draft_profile_text || '',
        application_info: defaultValues.application_info || '',
        application_link: defaultValues.application_link || '',
        references: defaultValues.references || '',
        opportunity_link: defaultValues.opportunity_link || '',
        flag_for_updates: defaultValues.flag_for_updates || '',
        next_check_in_date: defaultValues.next_check_in_date
          ? new Date(defaultValues.next_check_in_date)
          : null,
        questions_last_checked_at: defaultValues.questions_last_checked_at
          ? new Date(defaultValues.questions_last_checked_at)
          : null,
        gambling_proceeds: defaultValues.gambling_proceeds,
        is_international: defaultValues.is_international,
        only_funds_registered_charities:
          defaultValues.only_funds_registered_charities,
        max_rank_is_4: defaultValues.max_rank_is_4,
        tags: defaultValues.tags || [],
        general_info: defaultValues.general_info || '',
        structured_questions: defaultValues.structured_questions || [],
        iwi: defaultValues.iwi ?? [],
        email_address: defaultValues.email_address ?? null,
      }

      setValues(newValues)
      setInheritIwi(defaultValues.inherit_iwi_from_funder ?? true)
      if (!structuredQuestionsEditedRef.current) {
        setStructuredQuestions(defaultValues.structured_questions || [])
      }
      reset(newValues)
    }
  }, [defaultValues, reset])

  // Watch form values and sync with values state for display logic
  useEffect(() => {
    const subscription = watch((formValues) => {
      setValues(formValues as FormValues)
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const { data: funderIwiNamesData, isLoading: funderIwiNameIsLoading } =
    useList({
      resource: 'iwi',
      pagination: { pageSize: 100 },
      filters: [
        {
          field: 'id',
          operator: 'eq',
          value: defaultValues?.funder_iwi,
        },
      ],
      queryOptions: {
        enabled:
          defaultValues?.funder_iwi &&
          defaultValues?.funder_iwi.length > 0 &&
          !(query?.isLoading ?? false),
      },

      errorNotification: (error) => {
        return {
          message: 'Error getting funder iwi names',
          description:
            error?.message ||
            'An error occurred while fetching funder iwi names',
          type: 'error',
        }
      },
    })

  const { mutate: generateTags } = useCustomMutation<OpportunityTagResponse>()

  function handleGenerateTags() {
    if (!defaultValues) {
      return
    }
    setGenerating(true)
    setTagGenerationError(null)
    generateTags(
      {
        url: `/opportunities/tag/${defaultValues.uuid}`,
        method: 'post',
        values: {
          profile_text: values.profile_text,
        },
      },
      {
        onSuccess: async (response) => {
          openNotification?.({
            message: 'Tags generated successfully',
            type: 'success',
          })
          setGenerating(false)
          setTagResults(response.data || null)
          setTagsAccepted(false)
        },
        onError: (error: any) => {
          setGenerating(false)
          const status = error?.statusCode ?? error?.response?.status
          if (status === 400) {
            setTagGenerationError('Publish a profile before generating tags.')
          } else {
            openNotification?.({
              message: 'Failed to generate tags',
              type: 'error',
            })
          }
        },
      },
    )
  }

  function handleAcceptTag(tag: string) {
    if (values.tags?.includes(tag)) {
      return
    }
    const updatedTags = [...(values.tags || []), tag]
    setValue('tags', updatedTags)
  }

  function handleAcceptAllTags() {
    if (!tagResults) {
      return
    }

    const updatedTags = [...tagResults.tags]
    setValue('tags', updatedTags)
    setTagsAccepted(true)
  }

  // handle setting missing fields to defaults and continue submission
  // Note: flag_for_updates must be set by the user - no default is applied
  function handleSetDefaultsAndSubmit() {
    // Only proceed if flag_for_updates is set
    if (!flagForUpdatesValue) return

    // Set default date if flag requires it and none is set
    // Uses 14 days before earliest funding round, or 6 months if no rounds
    if (!nextCheckInDateValue && !flagAllowsNullDate) {
      const rounds = (fundingRoundsData?.data || []) as Array<{
        application_open_date?: string | null
      }>
      const suggestedDate = calculateSuggestedCheckInDate(
        rounds,
        alwaysOpenValue,
      )
      setValue('next_check_in_date', suggestedDate)
    }

    setMissingFieldsDialogOpen(false)

    // continue submission with current form values
    submitFormWithValues(getValues() as FormValues)
  }

  // handles actual form submission to backend
  function submitFormWithValues(formValues: FormValues) {
    try {
      if (
        formValues.flag_for_updates === 'Active' &&
        !formValues.profile_text?.trim()
      ) {
        alert(
          'A profile is required for an Active opportunity to show as green status.',
        )
        return
      }

      // Validate min amount
      const minValidation = validatePositiveAmount(
        formValues.min_amount,
        'Minimum amount',
      )
      if (!minValidation.isValid) {
        alert(minValidation.errorMessage)
        return
      }

      // Validate max amount
      const maxValidation = validatePositiveAmount(
        formValues.max_amount,
        'Maximum amount',
      )
      if (!maxValidation.isValid) {
        alert(maxValidation.errorMessage)
        return
      }

      // Validate amount range
      const rangeValidation = validateAmountRange(
        formValues.min_amount,
        formValues.max_amount,
      )
      if (!rangeValidation.isValid) {
        alert(rangeValidation.errorMessage)
        return
      }

      // Normalize amounts
      const { min_amount, max_amount } = normalizeAmounts(
        formValues.min_amount,
        formValues.max_amount,
        formValues.no_min_max_amount,
      )
      formValues.min_amount = min_amount
      formValues.max_amount = max_amount

      // validate tags
      const validTags =
        formValues.tags?.filter((tag) =>
          categoriesList.some((category) => category.value === tag),
        ) || []

      // An opportunity with a profile must carry at least one tag (GH #1427)
      if (formValues.profile_text?.trim() && validTags.length === 0) {
        alert(
          'At least one tag is required when the opportunity has a profile. Generate tags before saving.',
        )
        return
      }

      const formattedValues = {
        ...formValues,
        funder_uuid: formValues.funder_uuid,
        always_open: Boolean(formValues.always_open),
        is_relational: Boolean(formValues.is_relational),
        questions: formValues.questions
          ? formValues.questions.map((line) => line.trimEnd())
          : [],
        fund_name: normalizeFundName(formValues.fund_name),
        updated_by:
          user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`,
        next_check_in_date:
          formValues.next_check_in_date &&
          isValid(formValues.next_check_in_date)
            ? format(new Date(formValues.next_check_in_date), 'yyyy-MM-dd')
            : null,
        clear_next_check_in_date: formValues.next_check_in_date === null,
        // Only ship questions_last_checked_at when the admin actually touched
        // the picker. The `...formValues` spread above carries the field as a
        // Date object; JSON.stringify emits it as UTC, and the DB column is
        // `timestamp without time zone`, so an untouched save would shift the
        // stored day by the user's UTC offset. Setting to undefined drops the
        // key from the JSON body so the backend preserves the current value.
        ...(dirtyFields.questions_last_checked_at
          ? {
              questions_last_checked_at:
                formValues.questions_last_checked_at &&
                isValid(formValues.questions_last_checked_at)
                  ? format(
                      new Date(formValues.questions_last_checked_at),
                      'yyyy-MM-dd',
                    )
                  : null,
              clear_questions_last_checked_at:
                formValues.questions_last_checked_at === null,
            }
          : { questions_last_checked_at: undefined }),
        tags: validTags || [],
        structured_questions: structuredQuestions,
        inherit_iwi_from_funder: inheritIwi,
        iwi: inheritIwi ? [] : formValues.iwi,
      }

      onFinish(formattedValues)
    } catch (error) {
      console.error('Failed to update opportunity:', error)
      alert(
        'An error occurred while updating the opportunity. Please try again.',
      )
    }
  }

  // handles form submission with check for required fields
  async function handleSubmit(
    e: React.MouseEvent<HTMLButtonElement>,
    formValues: FormValues,
  ) {
    e.preventDefault()

    // next_check_in_date is not required when flag is 'Inactive'
    const flagAllowsNullDate = formValues.flag_for_updates === 'Inactive'

    const isDateMissing = !formValues.next_check_in_date && !flagAllowsNullDate
    const isFlagMissing = !formValues.flag_for_updates

    // check if required fields are missing
    if (isDateMissing || isFlagMissing) {
      // show dialog instead of directly submitting
      setMissingFieldsDialogOpen(true)
      return
    }

    // all required fields present, continue with normal submission
    submitFormWithValues(formValues)
  }

  // Use reference options hook
  const referencesValue = watch('references')
  const referenceOptions = useReferenceOptions(referencesValue)

  const isLoading = !defaultValues || funderListIsLoading || !categories

  return (
    <Edit
      saveButtonProps={{
        onClick: (e) => handleSubmit(e, getValues() as FormValues),
        disabled: isLoading,
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="form"
          sx={{ display: 'flex', flexDirection: 'column' }}
          autoComplete="off"
        >
          <Controller
            name="funder_uuid"
            control={control}
            rules={{ required: 'Funder is required' }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                options={funderOptions}
                value={currentFunder}
                getOptionLabel={(option: Funder) => option?.name || ''}
                isOptionEqualToValue={(option: Funder, value: Funder) =>
                  option?.uuid === value?.uuid
                }
                onChange={(_, value: Funder | null) => {
                  field.onChange(value?.uuid || '')
                }}
                noOptionsText="Start typing funder name..."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Funder"
                    margin="normal"
                    variant="outlined"
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
              />
            )}
          />
          <TextField
            {...register('fund_name')}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Fund Name"
            name="fund_name"
          />
          <Divider sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6"> Internal Notes and References</Typography>
          </Divider>

          <Controller
            name="flag_for_updates"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={missingFieldsDialogOpen && !field.value}
              >
                <InputLabel id="flag-for-updates-label">
                  Flag for Updates
                </InputLabel>
                <Select
                  {...field}
                  labelId="flag-for-updates-label"
                  label="Flag for Updates"
                  value={field.value || ''}
                >
                  {FLAG_OPTIONS.map((option) => (
                    <MenuItem key={option || 'empty'} value={option}>
                      {option || '(Not set)'}
                    </MenuItem>
                  ))}
                </Select>
                {missingFieldsDialogOpen && !field.value && (
                  <FormHelperText>
                    Flag for Updates is required - please select a value
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="next_check_in_date"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Next Check-in Date"
                  value={field.value}
                  onChange={(date: Date | null) => {
                    if (date && isValid(date)) {
                      // Normalize to the start of the day in local time
                      const localDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                      )
                      field.onChange(localDate)
                    } else {
                      field.onChange(null)
                    }
                  }}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                      required: !flagAllowsNullDate,
                      error:
                        missingFieldsDialogOpen &&
                        !field.value &&
                        !flagAllowsNullDate,
                      helperText:
                        missingFieldsDialogOpen &&
                        !field.value &&
                        !flagAllowsNullDate
                          ? 'Next check-in date is required'
                          : '',
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />

          <Controller
            name="questions_last_checked_at"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Questions Last Checked At"
                  value={field.value}
                  onChange={(date: Date | null) => {
                    if (date && isValid(date)) {
                      const localDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                      )
                      field.onChange(localDate)
                    } else {
                      field.onChange(null)
                    }
                  }}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                      helperText:
                        'Auto-bumps when the questions field changes. Override here if you verified the questions are still current.',
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />

          <Typography
            variant="body2"
            color={defaultValues?.funder_email_address ? 'text.secondary' : 'error'}
            sx={{ mt: 1 }}
          >
            {defaultValues?.funder_email_address
              ? `Funder email: ${defaultValues.funder_email_address}`
              : 'Funder has no email address'}
          </Typography>
          <TextField
            {...register('email_address', {
              setValueAs: (v: string) => v?.trim() || null,
              validate: (v) =>
                !v || EMAIL_REGEX.test(v) || 'Must be a valid email address',
            })}
            error={!!(errors as any)?.email_address}
            helperText={(errors as any)?.email_address?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="email"
            label="Email Address"
            name="email_address"
          />

          <TextField
            {...register('notes')}
            margin="normal"
            fullWidth
            multiline
            rows={4}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Notes"
            name="notes"
            sx={{
              '& .MuiOutlinedInput-input': {
                backgroundColor: '#fff',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                resize: 'vertical',
              },
            }}
          />

          <TextField
            {...register('references')}
            margin="normal"
            fullWidth
            multiline
            rows={4}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="References"
            name="references"
            value={values.references || ''}
            onChange={(e) => {
              setValues({ ...values, references: e.target.value })
              setValue('references', e.target.value)
            }}
          />

          <Typography variant="h6">
            Opportunity Link <PublicOutlinedIcon sx={{ ml: 0.25, mt: 1 }} />
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel id="opportunity-link-label-edit">
              Opportunity Link (shown to users)
            </InputLabel>
            <Select
              labelId="opportunity-link-label-edit"
              label="Opportunity Link (shown to users)"
              value={normalizeUrl(values.opportunity_link) ?? ''}
              onChange={(e) => {
                setValues({ ...values, opportunity_link: e.target.value })
                setValue('opportunity_link', e.target.value)
              }}
              disabled={referenceOptions.length === 0}
            >
              <MenuItem value="">
                <em>(None - fall back to funder website)</em>
              </MenuItem>

              {referenceOptions.length > 0 && <Divider />}

              {referenceOptions.map((url) => (
                <MenuItem key={url} value={url}>
                  {url}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {referenceOptions.length === 0
                ? 'Add URLs to References field first'
                : "This link will be shown to users when viewing matches. If not selected, users will see the funder's website instead."}
            </FormHelperText>
          </FormControl>
          <Divider sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6"> Details</Typography>
          </Divider>

          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6"> Hard Filters</Typography>
            <Typography variant="caption">
              {' '}
              Used to filter opportunities in matching.
              <br />
              Maximum Rank is 4, used to cap generic opportunities from scoring
              very high in matching.
            </Typography>
          </Box>
          <Controller
            name="gambling_proceeds"
            control={control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!value} />}
                label="Gambling Proceeds"
              />
            )}
          />
          <Controller
            name="is_international"
            control={control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!value} />}
                label="International"
              />
            )}
          />
          <Controller
            name="only_funds_registered_charities"
            control={control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!value} />}
                label="Only Funds Registered Charities"
              />
            )}
          />

          <Controller
            name="is_relational"
            control={control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!value} />}
                label="Relational"
              />
            )}
          />
          <Controller
            name="max_rank_is_4"
            control={control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!value} />}
                label="Maximum Rank is 4 (for generic opportunities)"
              />
            )}
          />

          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                value={
                  field.value
                    ? field.value
                        .map((tagValue: string) =>
                          categoriesList.find((cat) => cat.value === tagValue),
                        )
                        .filter(
                          (
                            tag: OpportunityTag | undefined,
                          ): tag is OpportunityTag => tag !== undefined,
                        )
                    : []
                }
                sx={{ my: 1 }}
                onChange={(event, newValues) => {
                  const newTags = newValues.map((value) =>
                    typeof value === 'string' ? value : value.value,
                  )
                  field.onChange(newTags)
                }}
                options={categoriesList}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.value
                }
                filterOptions={(options, params) =>
                  filterTagOptions(options, params.inputValue)
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    <Box>
                      <Typography variant="body1" fontWeight="500">
                        {option.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize="0.875rem"
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    label="Tags"
                    {...params}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: { backgroundColor: '#fff' },
                    }}
                  />
                )}
              />
            )}
          />

          <Button
            onClick={() => handleGenerateTags()}
            sx={{ width: '150px' }}
            disabled={!values.profile_text?.trim()}
          >
            {generating
              ? 'Generating...'
              : tagResults
              ? 'Regenerate'
              : 'Generate Tags'}
          </Button>
          {tagGenerationError && (
            <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5 }}>
              {tagGenerationError}
            </Typography>
          )}
          <Typography variant="caption" sx={{ mb: 1, fontStyle: 'italic' }}>
            The generated tags are based on the opportunity Profile section
            below - not the Draft Profile section. Please ensure that the
            Profile section is complete before generating tags.
            <br />
            Making edits to the Profile section will affect the generated tags.
            If you want to make adjustments to the profile, you can regenerate
            the tags after making the changes.
          </Typography>

          {tagResults && (
            <Box sx={{ mb: 2, bgcolor: '#f5f5f5', p: 2 }}>
              <Typography variant="body1">Generated Tags</Typography>
              <Divider sx={{ my: 1 }} />

              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}
              >
                {tagResults.tags.map((tag: string) => (
                  <Chip key={tag} label={tag} size="small" />
                ))}

                <Typography variant="body2" sx={{ mt: 1 }}>
                  <span style={{ fontWeight: 'bold' }}>Reasoning:</span>{' '}
                  {tagResults.reasoning}
                </Typography>
              </Box>
              <Divider sx={{ mt: 1 }} />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexWrap: 'wrap',
                  width: '400px',
                  gap: 0.5,
                  mt: 0.5,
                  mb: 1,
                }}
              >
                {!tagsAccepted &&
                  tagResults.tags.map((tag: string) => (
                    <React.Fragment key={tag}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="body2">{tag}</Typography>
                        {values.tags?.includes(tag) ? (
                          <Typography variant="body2">
                            Already in List
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => handleAcceptTag(tag)}
                          >
                            Accept
                          </Button>
                        )}
                      </Box>
                      <Divider />
                    </React.Fragment>
                  ))}
              </Box>

              {!tagsAccepted &&
                (() => {
                  // Check if there are any changes between current tags and generated tags
                  const toAdd = tagResults.tags.filter(
                    (tag) => !(values.tags || []).includes(tag),
                  )
                  const toRemove = (values.tags || []).filter(
                    (tag) => !tagResults.tags.includes(tag),
                  )
                  const hasChanges = toAdd.length > 0 || toRemove.length > 0

                  // Only show if there are changes
                  return (
                    hasChanges && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          border: '1px solid #ccc',
                          p: 1,
                          width: '400px',
                          bgcolor: 'white',
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ width: '250px' }}
                          onClick={() => handleAcceptAllTags()}
                        >
                          Accept and Replace All
                        </Button>
                        <Box
                          sx={{
                            mt: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          {(() => {
                            const toKeep = values.tags.filter((tag) =>
                              tagResults.tags.includes(tag),
                            )
                            return (
                              toKeep.length > 0 && (
                                <Typography variant="caption">
                                  <span style={{ fontWeight: 'bold' }}>
                                    Keep:
                                  </span>{' '}
                                  {toKeep.join(', ')}
                                </Typography>
                              )
                            )
                          })()}
                          {toRemove.length > 0 && (
                            <Typography variant="caption">
                              <span style={{ fontWeight: 'bold' }}>
                                Remove:
                              </span>{' '}
                              {toRemove.join(', ')}
                            </Typography>
                          )}

                          {toAdd.length > 0 && (
                            <Typography variant="caption">
                              <span style={{ fontWeight: 'bold' }}>Add:</span>{' '}
                              {toAdd.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )
                  )
                })()}
            </Box>
          )}

          {/* Iwi */}
          <Box sx={{ mb: 1, mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inheritIwi}
                  onChange={(e) => {
                    setInheritIwi(e.target.checked)
                    if (e.target.checked) setValue('iwi', [])
                  }}
                />
              }
              label="Inherit iwi from funder"
            />
            <IwiField
              control={control}
              iwiList={iwiList}
              selectedIwi={inheritIwi ? [] : selectedIwi}
              disabled={inheritIwi}
              onUserInput={() => setInheritIwi(false)}
            />
          </Box>
          {/* Inherited iwi from funder */}
          {funderIwiNamesData?.data && funderIwiNamesData.data.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight="medium" color="secondary">
                {inheritIwi ? 'Inheriting from funder:' : 'Funder iwi:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                {funderIwiNamesData.data.map((iwi: any, index: number) => (
                  <Typography key={iwi.id}>
                    {iwi.name}
                    {index < funderIwiNamesData.data.length - 1 ? ',' : ''}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ mt: 1 }} />
          <Divider />
          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">Amount Details</Typography>
            <Typography variant="caption">
              {' '}
              Used to filter opportunities in matching.
              <br />
              Description should be used for Internal reference.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 1 }}>
            <TextField
              {...register('amount')}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Amount Description"
              name="amount"
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Controller
                name="min_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    label="Minimum Amount"
                    value={field.value || ''}
                    disabled={values.no_min_max_amount}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  />
                )}
              />
              <Controller
                name="max_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    label="Maximum Amount"
                    value={field.value || ''}
                    disabled={values.no_min_max_amount}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  />
                )}
              />
            </Box>

            <Controller
              name="no_min_max_amount"
              control={control}
              render={({ field: { value, ...field } }) => (
                <FormControlLabel
                  label={'No minimum or maximum amount'}
                  disabled={
                    Number(values.max_amount) > 0 ||
                    Number(values.min_amount) > 0
                  }
                  control={<Checkbox {...field} checked={!!value} />}
                />
              )}
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">
              Timing Details <PublicOutlinedIcon sx={{ ml: 0.25 }} />
            </Typography>
            <Typography variant="caption">
              {' '}
              Description for Internal Use Only <br />
              Funding Rounds and/or Always Open flag used to filter
              opportunities in matching. <br />
              Upcoming funding rounds visible to Organisations in their match
              results as the Application Dates. <br />
              If always open, 'Always open' is visible in match results as the
              Application Dates.
            </Typography>
          </Box>
          <Controller
            name="always_open"
            control={control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!value} />}
                label="Always Open"
              />
            )}
          />

          <TextField
            {...register('timing')}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Timing Description"
            name="timing"
          />
          <Divider sx={{ my: 1 }} />

          <Typography variant="h6" sx={{ mt: 2 }}>
            {' '}
            Profile <PublicOutlinedIcon sx={{ ml: 0.25 }} />
          </Typography>
          <Typography variant="caption">
            {' '}
            Used to filter opportunities in matching. <br />
            Published Profile is visible to Organisations in match results.
          </Typography>

          <TextField
            {...register('draft_profile_text')}
            margin="normal"
            fullWidth
            multiline
            minRows={8}
            maxRows={16}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Draft Profile"
            name="draft_profile_text"
          />
          {values.uuid && (
            <Box
              sx={{
                mt: 1,
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <GenerateProfileButton
                opportunityUuid={values.uuid}
                onSuccess={(profile) => {
                  setValue('draft_profile_text', profile)
                }}
                hasExistingProfile={Boolean(values.draft_profile_text)}
                description={watch('description')}
                criteria={watch('criteria')}
                exclusions={watch('exclusions')}
                fund_name={watch('fund_name')}
              />
              {values.draft_profile_text && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Button
                    onClick={() => setTestProfileFormOpen(!testProfileFormOpen)}
                    disabled={!values.uuid || !values.draft_profile_text}
                    sx={{ mr: 1 }}
                  >
                    {testProfileFormOpen ? 'Close Test' : 'Test Profile'}
                  </Button>
                  {testProfileFormOpen && (
                    <Alert
                      severity="info"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <span style={{ fontWeight: 'bold' }}>Remember: </span>
                        Publish the draft profile when you are happy with the
                        results. Please double check the generated profile
                        before publishing.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
              <Box>
                <Button
                  onClick={() => {
                    setValue('profile_text', values.draft_profile_text || '')
                    setValue('draft_profile_text', '')
                  }}
                  disabled={!values.draft_profile_text}
                  sx={{ mr: 1 }}
                >
                  Publish
                </Button>
                <Button
                  onClick={() => {
                    setValue('draft_profile_text', '')
                  }}
                  disabled={!values.draft_profile_text}
                  color="error"
                >
                  Discard
                </Button>
              </Box>
            </Box>
          )}
          {testProfileFormOpen && (
            <TestProfile
              opportunityUuid={values.uuid}
              draftProfileText={values.draft_profile_text}
              funderUuid={values.funder_uuid}
            />
          )}
          <TextField
            {...register('profile_text')}
            margin="normal"
            fullWidth
            multiline
            minRows={8}
            maxRows={16}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Profile"
            name="profile_text"
          />
          <Alert severity="info">
            You can regenerate this after you save your changes
          </Alert>
          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">Profile Data</Typography>
            <Typography variant="caption">
              {' '}
              Used to generate a Profile for matching.
            </Typography>
          </Box>
          <TextField
            {...register('description')}
            margin="normal"
            fullWidth
            multiline
            rows={12}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Description"
            name="description"
          />

          <TextField
            {...register('criteria')}
            margin="normal"
            fullWidth
            multiline
            rows={4}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Criteria"
            name="criteria"
            sx={{
              '& .MuiOutlinedInput-input': {
                backgroundColor: '#fff',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                resize: 'vertical',
              },
            }}
          />

          <TextField
            {...register('exclusions')}
            margin="normal"
            fullWidth
            multiline
            rows={4}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Exclusions"
            name="exclusions"
            sx={{
              '& .MuiOutlinedInput-input': {
                backgroundColor: '#fff',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                resize: 'vertical',
              },
            }}
          />
          <Divider sx={{ my: 1 }} />
          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">
              General Info <PublicOutlinedIcon sx={{ ml: 0.25 }} />
            </Typography>
            <Typography variant="caption">
              {' '}
              Visible to Organisations in match results.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Information about the opportunity that should be separated from
              profile text and matching, but is also important for users to know
              about the opportunity.
            </Typography>
          </Box>

          <TextField
            {...register('general_info')}
            margin="normal"
            fullWidth
            multiline
            rows={8}
            type="text"
            InputLabelProps={{ shrink: true }}
            label="General Info (eg. Application process info you want to highlight)"
            name="general_info"
          />

          <Divider sx={{ my: 1 }} />
          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">
              Application Details <PublicOutlinedIcon sx={{ ml: 0.25 }} />
            </Typography>
            <Typography variant="caption">
              {' '}
              Visible to Organisations in application drafting. <br />
              Link to Application Page is visible to Organisations in email copy
              of application.
            </Typography>
          </Box>

          <TextField
            {...register('application_info')}
            margin="normal"
            fullWidth
            multiline
            rows={8}
            type="text"
            InputLabelProps={{ shrink: true }}
            label="Application Info (eg. Files to upload: )"
            name="application_info"
            sx={{
              '& .MuiOutlinedInput-input': {
                backgroundColor: '#fff',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                resize: 'vertical',
              },
            }}
          />
          <TextField
            {...register('application_link')}
            margin="normal"
            fullWidth
            multiline
            rows={2}
            type="text"
            InputLabelProps={{ shrink: true }}
            label="Website link to the application"
            name="application_link"
            sx={{ mb: 8 }}
          />

          <Box
            sx={{
              my: 2,
              display: 'flex',
              flexDirection: structuredQuestions.length > 0 ? 'row' : 'column',
              gap: 2,
              justifyContent: 'space-between',
              height: structuredQuestions.length > 0 ? '600px' : 'auto',
              resize: structuredQuestions.length > 0 ? 'vertical' : 'none',
              overflow: structuredQuestions.length > 0 ? 'auto' : 'visible',
              minHeight: structuredQuestions.length > 0 ? '300px' : 'auto',
              maxHeight: structuredQuestions.length > 0 ? '1200px' : 'none',
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0, // Important for flex children to scroll
              }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                Raw Questions
              </Typography>
              <HighlightedTextarea
                margin="normal"
                rows={structuredQuestions.length === 0 ? 20 : undefined}
                InputLabelProps={{ shrink: true }}
                label="Questions (add a blank line between each question)"
                name="questions"
                highlightRange={highlightRange}
                value={
                  (values.questions && values.questions.join('\n\n')) || ''
                }
                onChange={(e) => {
                  const newValue = e.target.value
                  const questionsArray = newValue ? newValue.split('\n\n') : []
                  setValues({
                    ...values,
                    questions: questionsArray,
                  })
                  // Also update react-hook-form state so changes are saved on submit
                  setValue('questions', questionsArray)
                }}
                sx={{
                  width: '100%',
                  flex: structuredQuestions.length > 0 ? 1 : 'none',
                  minHeight: 0, // Important for flex children to scroll
                  height: structuredQuestions.length > 0 ? '100%' : 'auto',
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                Structured Questions
              </Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <QuestionManager
                  rawQuestions={values.questions || []}
                  structuredQuestions={structuredQuestions}
                  onQuestionsChange={(questions) => {
                    structuredQuestionsEditedRef.current = true
                    setStructuredQuestions(questions)
                  }}
                  onActiveQuestionChange={handleActiveQuestionChange}
                  applicationLink={values.application_link}
                />
              </Box>
            </Box>
          </Box>

          {structuredQuestions.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <QuestionDiffChecker
                opportunityUuid={String(values.uuid)}
                existingStructuredQuestions={structuredQuestions}
                rawQuestions={values.questions || []}
                onAcceptQuestions={(questions) => {
                  structuredQuestionsEditedRef.current = true
                  setStructuredQuestions(questions)
                }}
                applicationLink={values.application_link}
              />
            </>
          )}

          <Dialog
            open={missingFieldsDialogOpen}
            onClose={() => setMissingFieldsDialogOpen(false)}
            aria-labelledby="missing-fields-dialog-title"
            aria-describedby="missing-fields-dialog-description"
          >
            <DialogTitle
              id="missing-fields-dialog-title"
              sx={{ textAlign: 'center' }}
            >
              Missing Required Fields
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
              <DialogContentText id="missing-fields-dialog-description">
                The following required fields are missing:
                <br />
                <br />
                {!nextCheckInDateValue && !flagAllowsNullDate && (
                  <>
                    • <strong>Next Check-in Date</strong> - will be set to{' '}
                    {defaultCheckInDateMessage}
                    <br />
                  </>
                )}
                {!flagForUpdatesValue && (
                  <>
                    • <strong>Flag for Updates</strong> - please select a value
                    <br />
                  </>
                )}
                <br />
                {flagForUpdatesValue
                  ? 'Would you like to set this default and continue?'
                  : 'Please close this dialog and select a flag for updates.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setMissingFieldsDialogOpen(false)}
                color="primary"
              >
                {flagForUpdatesValue ? 'No' : 'Close'}
              </Button>
              {flagForUpdatesValue && (
                <Button
                  onClick={handleSetDefaultsAndSubmit}
                  color="primary"
                  autoFocus
                >
                  Yes
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Edit>
  )
}
